import { useTranslation } from "react-i18next"

import type { PickerBrand } from "@/types/submissions"

/**
 * OKS-481 (§09) — Brand strip at the top of the TikTok post picker.
 *
 * Renders the CURRENT collab's brand avatar (24px) + name above the
 * creator's TikTok profile strip, anchoring the picker to the mission's
 * brand ("you're submitting to {brand}"). The brand identity is always
 * the collab's OWN brand (isolation P0) — the picker never surfaces
 * another brand's chrome.
 *
 * Distinct from the page-level `BrandStrip` (OKS-385, brand context +
 * crew switch on the missions home) — this one is picker-local and
 * non-interactive. A missing/broken logo collapses to a neutral block.
 */
interface Props {
  collabId: number
  brand: PickerBrand
}

export function PickerBrandStrip({ collabId, brand }: Props) {
  const { t } = useTranslation()

  return (
    <div
      data-testid={`tiktok-picker-brand-strip-${collabId}`}
      className="flex items-center gap-2 border-b pb-3"
      aria-label={t("submissions.picker.brand_strip_aria", {
        brand: brand.name,
      })}
    >
      {brand.avatar_url ? (
        <img
          src={brand.avatar_url}
          alt=""
          className="size-6 shrink-0 rounded-full object-cover"
          loading="lazy"
        />
      ) : (
        <div
          aria-hidden="true"
          className="bg-muted size-6 shrink-0 rounded-full"
        />
      )}
      <span className="truncate text-sm font-medium">{brand.name}</span>
    </div>
  )
}
